import React from 'react'
import {findDOMNode} from 'react-dom'
import styles from './MapPanel.scss'
import _ from 'lodash'
import {Map, MapSettings} from 'common/Map'
import {CuePopup} from 'common/Map/CuePopup'
import {CueTooltip} from 'common/Map/CueTooltip'
import {RouteLine} from 'common/Map/RouteLine'
import {SampleGraph} from 'common/SampleGraph'
import {SegmentPopup} from 'common/SegmentPopup'
import {PillGroup} from 'common/styleguide/PillGroup'
import {Icon} from 'common/Icon'
import {PopoverLinkMenu, PopoverLinkMenuItem} from 'common/styleguide/PopoverLinkMenu'
import {SAVE_AS_SEGMENT_DIALOG} from '../dialogs/SaveAsSegmentDialog'
// disabled for now, see comment about the Annotate menu item below for details
// import {NEW_ANNOTATION_DIALOG} from '../dialogs/AddAnnotationDialog'
import {NEW_ROUTE_DIALOG} from '../dialogs/NewRouteDialog'
import {Set, OrderedMap} from 'immutable'
import {Distance, Elevation} from 'common/units'
import {withMetricUnits} from 'common/units/MetricUnitsContext'
import {PhotoClusters} from 'common/Maplet/PhotoClusters'
import {connect} from 'react-redux'
import * as userActions from 'lib/api/currentUser'
import cx from 'classnames'
import {ExportSvgMenuItem} from 'common/GlobalNav/AdminMenu/Items'
const MetricAwareSampleGraph = withMetricUnits(SampleGraph)
const MetricAwareMap = withMetricUnits(Map)

// enums
const SG_COLLAPSED = 0
const SG_NORMAL = 1
const SG_EXPANDED = 2

// CSS constants
const SG_CONTROLS_HEIGHT = 45
// sample_graph.js subtracts 25px from the requested height if it can't find
// a div with the class .sg_bar in the DOM. This constant allows us to correct
// for that.
const SG_HEIGHT_CORRECTION = 25

const getSgHeight = (collapseState) => {
  switch (collapseState) {
    case SG_COLLAPSED:
      return SG_CONTROLS_HEIGHT
    case SG_EXPANDED:
      return 0 // 100%
    case SG_NORMAL:
    default:
      const MAX_HEIGHT = 280
      const MIN_HEIGHT = 120
      const proportional = window.innerHeight * 0.25
      return _.clamp(proportional, MIN_HEIGHT, MAX_HEIGHT)
  }
}

const sliceEqual = (a, b) => a && b && a[0] === b[0] && a[1] === b[1]

const RSPMapSettings = MapSettings({
  showDistanceMarkers: true
})

const userPrefMappings = {
  showDistanceMarkers: 'route_viewer_enable_distance_markers',
  metricUnits: 'metric_units',
  scrollWheelZoom: 'route_viewer_enable_scrollwheel_zoom'
}

const getMapSettingsForUser = (user) => {
  if (!user.id) { return RSPMapSettings }
  const prefs = Object.entries(userPrefMappings)
    .map(([clientKey, serverKey]) => [clientKey, user.preferences.get(serverKey, undefined)])
    .filter(([clientKey, value]) => value !== undefined)

  return RSPMapSettings.merge(prefs)
}

export class MapPanel extends React.PureComponent {
  constructor (props) {
    super(props)

    const {currentUser} = this.props
    // persist this to state because it only needs to be generated once, and
    // we don't want to trigger unecessary rerenders
    this.state = {
      defaultMapSettings: getMapSettingsForUser(currentUser),
      sgCollapseState: SG_NORMAL
    }
  }

  expandSg = () => {
    this.setState({sgCollapseState: Math.min(this.state.sgCollapseState + 1, 2)})
  }

  collapseSg = () => {
    this.setState({sgCollapseState: Math.max(this.state.sgCollapseState - 1, 0)})
  }

  onToggleMapSetting = (settingKey, newSettings) => {
    const {currentUser, setPreference} = this.props
    if (currentUser.id && userPrefMappings[settingKey]) {
      setPreference(userPrefMappings[settingKey], newSettings.get(settingKey))
    } else if (settingKey === 'metricUnits') {
      // set a temp preference for metric units even if we don't have a logged-in user
      setPreference(userPrefMappings[settingKey], newSettings.get(settingKey))
    }
  }

  render () {
    const {
      route,
      editable,
      suggestedBounds,
      highlightSlice,
      zoomOnHighlightSliceChange,
      hoverSlice,
      highlightPoint,
      isFullscreen,
      hoverCue,
      selectedCue,
      onCoursePointClicked,
      onSegmentSelected,
      onSegmentUnselected,
      onPointHighlighted,
      showDialog,
      flattenElevation,
      excludeFromMetrics,
      includeInMetrics,
      dirtyRoute,
      onSave,
      onRevert,
      currentUser
    } = this.props

    const {sgCollapseState, defaultMapSettings} = this.state

    const showSegmentPopup = highlightSlice
      ? ((!hoverSlice && highlightSlice) || (sliceEqual(hoverSlice, highlightSlice)))
      : false

    const mapHeight = sgCollapseState === SG_EXPANDED
      ? `calc(100% - ${getSgHeight(SG_NORMAL)}px)`
      : `calc(100% - ${getSgHeight(sgCollapseState)}px)`

    return (
      <div className={cx(styles.MapPanel, isFullscreen && styles.isFullscreen)}>
        <ExportSvgMenuItem />
        {route && <MetricAwareMap
          className={cx(styles.Map, sgCollapseState === SG_COLLAPSED ? styles.maximizedHeight : styles.normalHeight)}
          style={{zIndex: 0}}
          experimental
          showToolbar
          defaultMapSettings={defaultMapSettings}
          defaultOverlay={route.defaultBasemap}
          scrollWheelZoom={defaultMapSettings.scrollWheelZoom}
          suggestedBounds={suggestedBounds}
          highlightSlice={highlightSlice}
          highlightPoint={highlightPoint}
          onPointHighlighted={onPointHighlighted}
          height={mapHeight}
          zoomOutOnDeselect={false}
          zoomToHighlightSlice={zoomOnHighlightSliceChange}
          routes={[route]}
          onToggleSetting={this.onToggleMapSetting}>
          {Number.isFinite(hoverCue) && hoverCue !== selectedCue && route.coursePoints &&
            <CueTooltip coursePoint={route.coursePoints[hoverCue]} />}
          {Number.isFinite(selectedCue) && route.coursePoints &&
            <CuePopup
              autoPan
              coursePoint={route.coursePoints[selectedCue]}
              onRequestClose={() => onCoursePointClicked(null)} />}
          {showSegmentPopup && <SegmentPopup
            route={route}
            highlightSlice={highlightSlice}
            onRequestClose={onSegmentUnselected} />}
          {hoverSlice && <HoverSlice route={route} slice={hoverSlice} />}
          {route.photos && <PhotoClusters troute={route} clusterStrategy='route' />}
        </MetricAwareMap>}
        <SampleGraphContainer
          route={route}
          onExpand={this.expandSg}
          onCollapse={this.collapseSg}
          highlightSlice={highlightSlice}
          hoverSlice={hoverSlice}
          zoomOnHighlightSliceChange={zoomOnHighlightSliceChange}
          highlightPoint={highlightPoint}
          dirtyRoute={dirtyRoute}
          editable={editable}
          currentUser={currentUser}
          flattenElevation={flattenElevation}
          excludeFromMetrics={excludeFromMetrics}
          includeInMetrics={includeInMetrics}
          showDialog={showDialog}
          onSave={onSave}
          onRevert={onRevert}
          onSegmentUnselected={onSegmentUnselected}
          onPointHighlighted={onPointHighlighted}
          onSegmentSelected={onSegmentSelected}
          collapseState={sgCollapseState}
          height={getSgHeight(sgCollapseState)} />
      </div>
    )
  }
}

const mapStateToProps = state => ({
  currentUser: state.currentUser
})

const mapDispatchToProps = {
  setPreference: userActions.setPreference
}

export const MapPanelContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(MapPanel)

const metricsAvailable = OrderedMap({
  'ele': 'Ele',
  'grade': 'Grade'
})

class SampleGraphContainer extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      sgMetrics: Set(['ele']),
      parentHeight: null,
      expandSlice: null,
      sampleGraphZoomedIn: true
    }
    this._debouncedResize = _.debounce(this.onWindowResizeComplete, 500, {trailing: true})
  }

  componentDidMount () {
    this.layoutExpand()
    window.addEventListener('resize', this._debouncedResize)
  }

  componentWillUnmount () {
    this._unmounted = true
    window.removeEventListener('resize', this._debouncedResize)
  }

  onWindowResizeComplete = () => {
    // this check is needed because we debounce the resize listener, so in
    // some cases it can fire *after* the listener is removed in componentWillUnmount
    if (this._unmounted) { return }
    this.layoutExpand()
  }

  layoutExpand = () => {
    const node = findDOMNode(this)
    if (!node) { return }
    const parentHeight = node.parentElement.clientHeight
    console.log('layoutExpand', parentHeight)
    if (parentHeight !== this.state.parentHeight) {
      this.setState({parentHeight})
    }
  }

  zoomInClicked = () => {
    this.setState({sampleGraphZoomedIn: true})
  }

  zoomOutClicked = () => {
    this.setState({sampleGraphZoomedIn: false})
  }

  onSegmentUnselected = () => {
    this.setState({sampleGraphZoomedIn: true})
    this.props.onSegmentUnselected()
  }

  render () {
    const {
      route,
      currentUser,
      highlightSlice,
      hoverSlice,
      zoomOnHighlightSliceChange,
      dirtyRoute,
      editable,
      flattenElevation,
      excludeFromMetrics,
      includeInMetrics,
      showDialog,
      onSave,
      onRevert,
      onPointHighlighted,
      onSegmentSelected,
      highlightPoint,
      collapseState,
      height,
      onExpand,
      onCollapse
    } = this.props

    const {sgMetrics, parentHeight, expandSlice, sampleGraphZoomedIn} = this.state

    // we need to always emit DOM here in order to grab a ref to the parent
    if (!route) { return <div /> }

    const showExcludeFromMetrics = highlightSlice && route.rawPoints.slice(highlightSlice[0], highlightSlice[1]).some(p => !p.k) // Some points in the highlighted portion are included in metrics
    const showIncludeInMetrics = highlightSlice && route.rawPoints.slice(highlightSlice[0], highlightSlice[1]).some(p => !!p.k) // Some points in the highlighted portion have been excluded from metrics
    const expanded = collapseState === SG_EXPANDED
    const normal = collapseState === SG_NORMAL
    const collapsed = collapseState === SG_COLLAPSED

    const containerHeight = expanded ? undefined : height
    const sgHeight = height - SG_CONTROLS_HEIGHT + SG_HEIGHT_CORRECTION
    const expandGraphHeight = (parentHeight - SG_CONTROLS_HEIGHT) / 2 + SG_HEIGHT_CORRECTION

    return <div className={cx(styles.sgFloat, expanded && styles.expanded)} style={{height: containerHeight}}>

      <div className={styles.sgControls}>
        {/* left-aligned */}
        <div className={styles.sgControlsLeft}>
          <Icon className={cx(styles.expandControl, expanded && styles.disabled)} i='circle-expand' size={24} onClick={onExpand} />
          <Icon className={cx(styles.expandControl, collapsed && styles.disabled)} i='circle-collapse' size={24} onClick={onCollapse} />
          {normal && <PillGroup
            inline
            options={metricsAvailable}
            selected={sgMetrics}
            onChange={sgMetrics => this.setState({sgMetrics})} />}
          {(!highlightSlice || expanded) && <span className={styles.overallMetrics}>
            {<Distance value={route.distance} />}
            {' · '}
            +<Elevation short value={route.elevationGain} />
            {' / '}
            -<Elevation short value={route.elevationLoss} />
          </span>}
        </div>
        {/* right-aligned */}
        <div>
          {normal && !highlightSlice && !dirtyRoute && <span>Drag on graph to zoom/select</span>}
          {normal && highlightSlice && <span className={styles.zoomControls}>
            <Icon i='zoom-in' size={15} className={cx(styles.zoomControl, sampleGraphZoomedIn && styles.disabled)} onClick={this.zoomInClicked} />
            <Icon i='zoom-out' size={15} className={cx(styles.zoomControl, !sampleGraphZoomedIn && styles.disabled)} onClick={this.zoomOutClicked} />
          </span>}
          {highlightSlice && editable && <SelectionMenu title='Edit'>
            <PopoverLinkMenuItem onClick={() => flattenElevation()}>Flatten selection</PopoverLinkMenuItem>
            {showExcludeFromMetrics && <PopoverLinkMenuItem onClick={excludeFromMetrics}>Exclude from metrics</PopoverLinkMenuItem>}
            {showIncludeInMetrics && <PopoverLinkMenuItem onClick={includeInMetrics}>Include in metrics</PopoverLinkMenuItem>}
          </SelectionMenu>}
          {highlightSlice && currentUser.id && <SelectionMenu title='Save as'>
            <PopoverLinkMenuItem onClick={() => showDialog(SAVE_AS_SEGMENT_DIALOG)}>Segment</PopoverLinkMenuItem>
            {/* "Annotate" is disabled for now since we don't show annotations in the sidebar. */}
            {/* We may choose to re-enable it depending on the resolution to the email chain discussion "Deprecating Annotate option on RSP" */}
            {/* <PopoverLinkMenuItem onClick={() => showDialog(NEW_ANNOTATION_DIALOG)}>Annotation</PopoverLinkMenuItem> */}
            <PopoverLinkMenuItem onClick={() => showDialog(NEW_ROUTE_DIALOG)}>A new route</PopoverLinkMenuItem>
          </SelectionMenu>}
          {dirtyRoute && editable && <React.Fragment><a onClick={onSave}>Save changes</a> or <a onClick={onRevert}>Revert</a>?</React.Fragment>}
          {highlightSlice && <a onClick={this.onSegmentUnselected}>
            Clear Selection
          </a>}
        </div>
      </div>
      <div className={styles.wrap}>
        {expanded && <div style={{flex: '1'}} ref={this.expandContainerRef}>
          {parentHeight && <MetricAwareSampleGraph
            onSegmentSelected={s => this.setState({expandSlice: s})}
            onSegmentUnselected={() => this.setState({expandSlice: null})}
            highlightSlice={expandSlice}
            hoverSlice={hoverSlice}
            zoomOnHighlightSliceChange={zoomOnHighlightSliceChange}
            metricsEnabled={Set(['ele'])}
            dragMode='zoom'
            height={expandGraphHeight}
            zoomedIn={sampleGraphZoomedIn}
            rawPoints={route.rawPoints} />}
          {parentHeight && <MetricAwareSampleGraph
            onSegmentSelected={s => this.setState({expandSlice: s})}
            onSegmentUnselected={() => this.setState({expandSlice: null})}
            highlightSlice={expandSlice}
            hoverSlice={hoverSlice}
            zoomOnHighlightSliceChange={zoomOnHighlightSliceChange}
            metricsEnabled={Set(['grade'])}
            dragMode='zoom'
            height={expandGraphHeight}
            zoomedIn={sampleGraphZoomedIn}
            rawPoints={route.rawPoints} />}
        </div>}
        {normal && <MetricAwareSampleGraph
          metricsEnabled={sgMetrics}
          onPointHighlighted={onPointHighlighted}
          onSegmentSelected={onSegmentSelected}
          highlightSlice={highlightSlice}
          hoverSlice={hoverSlice}
          zoomOnHighlightSliceChange={zoomOnHighlightSliceChange}
          highlightPoint={highlightPoint}
          dragMode='select'
          height={sgHeight}
          zoomedIn={sampleGraphZoomedIn}
          rawPoints={route.rawPoints} />}
      </div>
    </div>
  }
}

const HoverSlice = ({route, slice}) => [
  <RouteLine
    key='outline'
    route={route}
    slice={slice}
    color='#FFF'
    weight={7}
    showStartMarker={false}
    showEndMarker={false}
    showPOIs={false} />,
  <RouteLine
    key='highlight'
    route={route}
    slice={slice}
    color='#0a68bb'
    weight={3}
    showStartMarker={false}
    showEndMarker={false}
    showPOIs={false} />
]

class SelectionMenu extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      expanded: false
    }
  }

  setRef = (ref) => {
    this._menu = ref
  }

  toggleMenu = () => {
    this.setState({expanded: !this.state.expanded})
  }

  render () {
    const {children, title} = this.props
    const {expanded} = this.state

    return (
      <React.Fragment>
        <span ref={this.setRef} onClick={this.toggleMenu} className={styles.menuButton}>{title} {expanded ? <Icon i='ios-arrow-up' /> : <Icon i='ios-arrow-down' />}</span>
        {expanded && <PopoverLinkMenu
          container={window.body}
          target={() => this._menu}
          insideMargin={false}
          placement='bottom'
          noArrow
          slim
          onRequestClose={this.toggleMenu}>
          {children}
        </PopoverLinkMenu>}
      </React.Fragment>
    )
  }
}
